import {
  CONFIG_OPTION_TOAST_ERROR,
  CONFIG_OPTION_TOAST_NORMAL,
} from "common/toast";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Col,
  Container,
  Input,
  Label,
  Progress,
  Row,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";
import { useRole } from "components/Hooks/UserHooks";
import { ROLES_FOR_APP, isHavePermissionRole } from "helpers/role";
import { getUtilityDetail, postFacility, putFacility } from "store/facility/thunk";
import UploadImageSingle from "components/Common/UploadImageSingle";

export interface Props {
  isModal?: boolean;
  id?: string;
  isCopy?: boolean;
  triggerRefresh?: () => void;
  setLinkImage?: any;
  isCloseModal?: any;
  rowSelected?: any;
  barId?: string
}

registerLocale("en", en);
registerLocale("ko", ko);

const UtilityForm = ({
  isModal = false,
  id = "",
  isCopy = false,
  triggerRefresh,
  setLinkImage,
  isCloseModal,
  rowSelected,
  barId
}: Props) => {
  const { t, i18n } = useTranslation();
  const { userPermissions } = useRole();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false);

  const handleSubmit = async (values: any) => {
    try {
      isCloseModal && isCloseModal();

      setIsLoading((_prev) => true);
      const data = {
        ...values,
        type: "customize",
        bar_id: barId,
        bar_status: values?.status === false ? 0 : 1
      };

      const response: any = id
        ? await putFacility(id, data)
        : await postFacility(data);

      if (response?.data) {
        setIsLoading((_prev) => false);
        toast(
          `${t("The process has been completed.")}`,
          CONFIG_OPTION_TOAST_NORMAL
        );
        triggerRefresh && triggerRefresh();
      } else {
        setIsLoading((_prev) => false);
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsLoading((_prev) => false);
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  const validationSchema = Yup.object({
    name: Yup.object().shape({
      en: Yup.string().required(`${t("This field is required")}`),
      ko: Yup.string().required(`${t("This field is required")}`),
    }),
    icon: Yup.string().required(`${t("This field is required")}`),
  });


  const formik = useFormik({
    initialValues: {
      name: {
        ko: "",
        en: ""
      },
      icon: "",
      bar_status: 1
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  const handleSetValueForm = (valueDefault: any) => {
    formik.setFieldValue("icon", valueDefault?.icon || "");
    formik.setFieldValue("name", valueDefault?.name || {
      en: "",
      ko: ""
    });
  };

  const handleCallUtilityDetail = async (idItem: string) => {
    try {
      if (!idItem) {
        return;
      }
      setIsLoadingDetail((_prev) => true);
      const resDetail: any = await getUtilityDetail(idItem);
      if (resDetail?.data) {
        handleSetValueForm(resDetail?.data);
        setIsLoadingDetail((_prev) => false);
      }else {
        setIsLoadingDetail((_prev) => false);
      }
    } catch (error: any) {
      setIsLoadingDetail((_prev) => false);
      return error;
    }
  };

  useEffect(() => {
    if(rowSelected?.id) {
      handleCallUtilityDetail(rowSelected?.id)
      // handleSetValueForm(rowSelected)
    }
  }, [rowSelected]);


  useEffect(() => {
    document.title = `${t("Utilities")} - ${t("Bar Detail")} | Baive`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);


  return (
    <React.Fragment>
      {isLoadingDetail && (
        <div
          style={{
            position: "absolute",
            zIndex: 3,
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            backgroundColor: "rgb(164 164 164 / 36%)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Spinner size="sm" color="primary"></Spinner>
        </div>
      )}
      <div>
        <Container fluid className="px-0">
          <div className="mb-4">
            <Progress
              value={
                100 - (100 / 3) * Number(Object.keys(formik.errors).length)
              }
              color="primary"
              className="animated-progress progress-sm"
            />
          </div>
          <form onSubmit={formik.handleSubmit}>
            <Row>
              <Col lg={12}>
                <div className="mb-3">
                  <Label htmlFor="nameInput" className="form-label">
                    {t("Name")} <span className="text-danger">*</span>
                  </Label>
                  <Input
                    name="name.en"
                    type="text"
                    className="form-control"
                    id="nameInput"
                    placeholder={t("Enter utility name")}
                    value={formik?.values?.name?.en}
                    onChange={(event: any) =>
                      formik.setFieldValue("name.en", event?.target?.value)
                    }
                  />
                  {formik?.touched?.name?.en && formik?.errors?.name?.en ? (
                    <div className="text-danger mt-2">
                      {formik?.errors?.name?.en}
                    </div>
                  ) : null}
                </div>
              </Col>

              <Col lg={12}>
                <div className="mb-3">
                  <Label htmlFor="nameKoreanInput" className="form-label">
                    {t("Name (Korean)")} <span className="text-danger">*</span>
                  </Label>
                  <Input
                    name="name.ko"
                    type="text"
                    className="form-control"
                    id="nameKoreanInput"
                    placeholder={t("Enter utility name (Korean)")}
                    value={formik?.values?.name?.ko}
                    onChange={(event: any) =>
                      formik.setFieldValue("name.ko", event?.target?.value)
                    }
                  />
                  {formik?.touched?.name?.ko && formik?.errors?.name?.ko ? (
                    <div className="text-danger mt-2">
                      {formik?.errors?.name?.ko}
                    </div>
                  ) : null}
                </div>
              </Col>

              <Col lg={12}>
                <div className="mb-3">
                  <Label htmlFor="nameKoreanInput" className="form-label">
                    {t("Status")}
                  </Label>
                  
                  <div
                    className="form-check form-switch form-switch-sm text-center"
                    dir="ltr"
                  >
                    <input
                      type="checkbox"
                      className="form-check-input"
                      name="status"
                      checked={!!formik?.values?.bar_status}
                      onChange={(e) => {
                        const value = e?.target?.checked;
                        formik.setFieldValue("bar_status", !!value)
                      }}
                    />
                  </div>
                </div>
              </Col>

              <Col lg={12}>
                <div className="mb-3">
                  <Label htmlFor="imageInput" className="form-label">
                    {t("Image")} <span className="text-danger">*</span>
                  </Label>

                  <UploadImageSingle
                    data={formik.getFieldProps("icon")?.value}
                    setLinkImage={null}
                    formik={formik}
                    isMultiple={false}
                    uploadProps={{
                      name: "files",
                      accept: "image/png,image/jpeg,image/*",
                      beforeUpload: () => false,
                      headers: {
                        authorization: "authorization-text",
                      },
                    }}
                    formikNameImage={"icon"}
                  />
                </div>
              </Col>

              <Col lg={12}>
                <div className="hstack gap-2 justify-content-end">
                  {isHavePermissionRole(
                    ROLES_FOR_APP.USER_UPDATE,
                    userPermissions
                  ) && (
                    <button
                      className="btn btn-primary fs-14"
                      color="success"
                      type="submit"
                      disabled={isLoading}
                      style={{ width: "140px" }}
                    >
                      {isLoading ? (
                        <Spinner size="sm" className="me-2"></Spinner>
                      ) : (
                        <>
                          {id
                            ? t("Button Update Utility")
                            : t("Button Create Utility")}
                        </>
                      )}
                    </button>
                  )}
                  {/* <button className="btn btn-soft-secondary fs-14" type="button" color="light" onClick={onCloseClick} disabled={isLoading}>
                        <i className="ri-indeterminate-circle-line align-bottom me-1"></i>
                        {t('Button Close')}
                    </button> */}
                </div>
              </Col>
            </Row>
          </form>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default UtilityForm;
